const LIFESPAN = 540; // 9s at 60fps

/**
 * Space currency — amber / solar crystal shards.
 * Collectible with magnet pull + generous pickup radius.
 */
export class Coin {
    constructor(x, y) {
        this.reset(x, y);
    }

    reset(x, y) {
        this.x = x;
        this.y = y;
        this.radius = 10;
        this.collected = false;
        this.vx = (Math.random() - 0.5) * 4.5;
        this.vy = (Math.random() - 0.5) * 4.5;
        this.friction = 0.91;
        this.age = 0;
        this.lifespan = LIFESPAN;
        this.spin = Math.random() * Math.PI * 2;
        this.spinSpeed = 0.04 + Math.random() * 0.05;
        this.bob = Math.random() * Math.PI * 2;
        // Crystal style variant
        this.style = Math.random() < 0.55 ? 'cluster' : 'shard';
        this.value = 1;
    }

    /**
     * Returns: 'collected' | 'expired' | null
     */
    update(player) {
        this.age++;
        this.spin += this.spinSpeed;
        this.bob += 0.07;

        this.vx *= this.friction;
        this.vy *= this.friction;
        this.x += this.vx;
        this.y += this.vy;

        const dx = player.x - this.x;
        const dy = player.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.001;

        // Base soft attract + magnet power-up
        const magnetR = Math.max(70, (player.magnetRange || 0) + 55);
        if (dist < magnetR) {
            const pull = 0.22 + (1 - dist / magnetR) * 0.55;
            this.vx += (dx / dist) * pull;
            this.vy += (dy / dist) * pull;
        }

        if (dist < player.size * 0.65 + this.radius + 6) {
            this.collected = true;
            return 'collected';
        }

        if (this.age >= this.lifespan) return 'expired';
        return null;
    }

    draw(ctx) {
        const progress = this.age / this.lifespan;
        let alpha = progress > 0.78 ? 1 - (progress - 0.78) / 0.22 : 1;
        if (progress > 0.78 && Math.sin(this.age * 0.5) < 0) alpha *= 0.55;

        const bobY = Math.sin(this.bob) * 2.2;
        const scale = progress > 0.65
            ? 1 + Math.sin(this.age * 0.35) * 0.12 * ((progress - 0.65) / 0.35)
            : 1;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(this.x, this.y + bobY);
        ctx.rotate(this.spin);
        ctx.scale(scale, scale);

        // Soft bloom (no heavy shadowBlur)
        const bloom = ctx.createRadialGradient(0, 0, 0, 0, 0, this.radius * 2.4);
        bloom.addColorStop(0, 'rgba(255, 200, 80, 0.45)');
        bloom.addColorStop(0.5, 'rgba(255, 160, 40, 0.12)');
        bloom.addColorStop(1, 'transparent');
        ctx.fillStyle = bloom;
        ctx.beginPath();
        ctx.arc(0, 0, this.radius * 2.4, 0, Math.PI * 2);
        ctx.fill();

        if (this.style === 'cluster') {
            this._drawCluster(ctx);
        } else {
            this._drawShard(ctx);
        }

        ctx.restore();
    }

    _drawShard(ctx) {
        const s = this.radius;
        // Main amber crystal facet
        const body = ctx.createLinearGradient(-s, -s, s, s);
        body.addColorStop(0, '#fff3c4');
        body.addColorStop(0.35, '#ffcc44');
        body.addColorStop(0.7, '#e89a12');
        body.addColorStop(1, '#a85e08');
        ctx.fillStyle = body;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.15);
        ctx.lineTo(s * 0.72, -s * 0.15);
        ctx.lineTo(s * 0.45, s * 0.95);
        ctx.lineTo(-s * 0.5, s * 0.7);
        ctx.lineTo(-s * 0.78, -s * 0.2);
        ctx.closePath();
        ctx.fill();

        // Bright edge
        ctx.strokeStyle = 'rgba(255, 245, 200, 0.9)';
        ctx.lineWidth = 1.4;
        ctx.stroke();

        // Highlight facet
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.beginPath();
        ctx.moveTo(0, -s * 0.95);
        ctx.lineTo(s * 0.28, -s * 0.2);
        ctx.lineTo(-s * 0.15, -s * 0.05);
        ctx.closePath();
        ctx.fill();

        // Dark facet for depth
        ctx.fillStyle = 'rgba(120, 60, 0, 0.35)';
        ctx.beginPath();
        ctx.moveTo(s * 0.45, s * 0.2);
        ctx.lineTo(s * 0.45, s * 0.95);
        ctx.lineTo(-s * 0.15, s * 0.55);
        ctx.closePath();
        ctx.fill();
    }

    _drawCluster(ctx) {
        const s = this.radius;
        // Three overlapping shards
        const shards = [
            { rot: 0, sc: 1, a: 1 },
            { rot: 0.9, sc: 0.7, a: 0.85 },
            { rot: -1.1, sc: 0.55, a: 0.75 },
        ];
        for (const sh of shards) {
            ctx.save();
            ctx.rotate(sh.rot);
            ctx.scale(sh.sc, sh.sc);
            ctx.globalAlpha *= sh.a;
            const body = ctx.createLinearGradient(0, -s, 0, s);
            body.addColorStop(0, '#fff6d0');
            body.addColorStop(0.4, '#ffc933');
            body.addColorStop(1, '#c97810');
            ctx.fillStyle = body;
            ctx.beginPath();
            ctx.moveTo(0, -s);
            ctx.lineTo(s * 0.55, s * 0.35);
            ctx.lineTo(0, s * 0.85);
            ctx.lineTo(-s * 0.55, s * 0.35);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = 'rgba(255, 240, 180, 0.85)';
            ctx.lineWidth = 1.1;
            ctx.stroke();
            ctx.restore();
        }
        // Core glint
        ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
        ctx.beginPath();
        ctx.arc(-s * 0.1, -s * 0.15, s * 0.18, 0, Math.PI * 2);
        ctx.fill();
    }
}
